import { OmniDismissButton } from "@/components/artifacts/OmniDismissButton.tsx";
import { Button } from "@/components/ui/button.tsx";
import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";

/* Its own module rather than exports hanging off one of the cards, because the
   others would then import from whichever card happened to hold them — a
   dependency that reads as "the heavy biscuit is the original and the rest are
   its variants". They are not: they are states of one surface, and what they
   share belongs to the surface rather than to any one state. */

/**
 * The outer chrome every Omni card wears.
 *
 * Matched to the Omnibox form's own chrome so the card state and the composer
 * state share one outer rectangle: border, padding, rounding and shadow are the
 * same, and the user moving between them sees the box stay put rather than
 * resize. Every child sits inside the `p-3 @md:p-4` padding, so visible gutters
 * line up with the standalone composer too.
 *
 * A constant rather than three copies because the three cards occupy the same
 * slot one at a time. A value that drifts in one of them does not look like a
 * bug in that card; it looks like the card jumping as the queue pages. Reached
 * only through {@link OmniCardChrome} for the same reason: a card that wore the
 * class without the layer would page in looking identical and handle Escape
 * differently.
 */
const OMNI_CARD_CHROME_CLASS =
  "relative flex flex-col gap-3 h-fit max-w-full mx-4 md:mx-0 border bg-popover rounded-2xl p-3 @md:p-4 shadow-bbl text-popover-foreground";

/**
 * The chrome, plus the one thing every Omni card has to get right about Escape:
 * dismiss itself, unless something is layered over it.
 *
 * A `DismissableLayer` rather than a `document` listener of the card's own
 * because "is something above me" is a question only Radix's layer stack can
 * answer. The three cards each used to bind Escape on `document` in the capture
 * phase, which orders them against Radix by registration alone — a card mounted
 * before a dialog opens always runs first — and then called `preventDefault()`.
 * `DismissableLayer` dismisses only while `defaultPrevented` is false, so the
 * card silently took the key in the dialog's place and threw the review away
 * (BIG-7904). Joining the stack makes the lightbox, dialog, select or menu the
 * user actually aimed at the highest layer, and this one stands down.
 *
 * `stopPropagation` but never `preventDefault`: the former is what keeps the key
 * from bubbling on to the screen-level back shortcut, which would navigate the
 * user out from under a card they were only trying to close — and it is safe,
 * because Radix listens in the capture phase where it never arrives. Radix
 * prevents the default itself, on the one path where doing so is correct.
 *
 * Outside interaction is deliberately inert. A review is dismissed by Escape or
 * by its own control, not by clicking away — the user reaching for the thread
 * behind it has not decided anything.
 */
export const OmniCardChrome = ({
  onDismiss,
  children,
}: {
  /** Absent on a card with nothing to dismiss, which then leaves Escape alone
   *  rather than swallowing it into a no-op. */
  onDismiss?: () => void;
  children: ReactNode;
}) => (
  <DismissableLayer
    className={OMNI_CARD_CHROME_CLASS}
    onEscapeKeyDown={(e) => {
      if (onDismiss) e.stopPropagation();
    }}
    onInteractOutside={(e) => e.preventDefault()}
    onDismiss={onDismiss}
  >
    {children}
  </DismissableLayer>
);

/** Where a card sits in the queue, and how to step off it. */
export type OmniCardPagination = {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
};

/**
 * The review-queue state the header draws: the "· K decided" hint, and the
 * "Skip" button the last card offers in place of next.
 *
 * A card carries more than this — what it has staged, how to stage the next
 * thing — and intersects this type rather than redeclaring the part the header
 * reads, so a field added here cannot be missed by one of them.
 */
export type OmniCardStaging = {
  decidedCount: number;
  isSubmitting: boolean;
  /** End the review with what was staged, leaving undecided cards pending —
   *  the "Skip" button. */
  onSkip: () => void;
};

/**
 * What a card can do about being expanded, resolved by the caller.
 *
 * Expandability is a capability rather than a default: a heavy artifact without
 * a `view` would open a blank lightbox, so it passes nothing and no control is
 * drawn. The header cannot decide this for itself, and deliberately does not try
 * — it only draws what it is handed.
 */
export type OmniCardExpand = {
  onExpand: () => void;
  /** Names the destination for a screen reader, e.g. "Expand Design Sync". */
  label: string;
};

/**
 * The expand control itself.
 *
 * Its own component because a card draws it in one of two places depending on
 * whether it is in a queue: inside the pager when there is one, on the title
 * line when there is not. Two renderings of one control is how the icon and the
 * label drift apart.
 */
export const OmniCardExpandButton = ({
  expand,
  disabled,
  statsigName,
}: {
  expand: OmniCardExpand;
  disabled?: boolean;
  statsigName: string;
}) => (
  <Button
    variant="ghostPopover"
    size="action"
    onClick={expand.onExpand}
    disabled={disabled}
    aria-label={expand.label}
    data-statsig-name={statsigName}
  >
    <ArrowUpRight className="size-4" />
  </Button>
);

/**
 * The carousel pager the Omni biscuits wear: counter on the left, then prev,
 * next-or-Skip, expand and dismiss anchored right.
 *
 * One header for the two of them. They drew it separately, and the group is
 * anchored right, so a control missing from one copy shifts every control
 * beside it: that is how the answered form came to draw its next button exactly
 * where the heavy biscuit draws expand, turning a click meant to page the queue
 * into one that opens the lightbox.
 *
 * The stepped form card keeps a row of its own. Its counter nests a question
 * position inside the artifact position, its prev steps a field rather than a
 * card, and its terminal button is "Done" under its own analytics name — three
 * differences that would have to become parameters here, leaving something that
 * no longer describes one thing. It draws the same controls in the same slots
 * instead, from the same {@link OmniCardExpandButton} and `OmniDismissButton`,
 * which is what the user is reading when they page between the cards.
 *
 * `statsigPrefix` keeps each card's existing analytics names intact
 * (`artifact-biscuit-prev`, `artifact-form-biscuit-prev`), so consolidating the
 * markup does not silently rename two event streams.
 */
export const OmniCardHeader = ({
  pagination,
  staging,
  expand,
  onDismiss,
  statsigPrefix,
}: {
  pagination: OmniCardPagination;
  staging?: OmniCardStaging;
  expand?: OmniCardExpand;
  onDismiss?: () => void;
  statsigPrefix: string;
}) => {
  const isLastInQueue = pagination.index === pagination.total - 1;
  const disabled = staging?.isSubmitting;

  return (
    /* The two 5px values are the same number for the same reason: the icon
       buttons are 26px boxes around 16px glyphs, so each carries 5px of its own
       inset. `ps` gives the text that inset so it starts where the glyphs sit,
       and `-mb` hands back the overhang the taller row would otherwise add below
       the text, which left more air above the title than below it. */
    <div className="flex items-center justify-between text-xs text-muted-foreground ps-[5px] -mb-[5px]">
      <span className="tabular-nums">
        {pagination.index + 1} of {pagination.total}
        {staging ? ` · ${staging.decidedCount} decided` : null}
      </span>
      <div className="flex items-center gap-1">
        {/* Drawn only where there is a card behind it. Prev is the leftmost
            child of a group anchored to the right of a `justify-between` row, so
            its absence leaves every control beside it in place. */}
        {pagination.index > 0 && (
          <Button
            variant="ghostPopover"
            size="action"
            onClick={pagination.onPrev}
            disabled={disabled}
            aria-label="Previous artifact"
            data-statsig-name={`${statsigPrefix}-prev`}
          >
            <ChevronLeft className="size-4" />
          </Button>
        )}
        {isLastInQueue && staging ? (
          <Button
            variant="secondary"
            size="xs"
            onClick={staging.onSkip}
            disabled={staging.isSubmitting}
            data-statsig-name={`${statsigPrefix}-skip`}
          >
            {staging.isSubmitting ? "Submitting…" : "Skip"}
          </Button>
        ) : (
          <Button
            variant="ghostPopover"
            size="action"
            onClick={pagination.onNext}
            disabled={isLastInQueue || disabled}
            aria-label="Next artifact"
            data-statsig-name={`${statsigPrefix}-next`}
          >
            <ChevronRight className="size-4" />
          </Button>
        )}
        {expand && (
          <OmniCardExpandButton
            expand={expand}
            disabled={disabled}
            statsigName={`${statsigPrefix}-expand`}
          />
        )}
        {onDismiss && (
          <OmniDismissButton
            onDismiss={onDismiss}
            disabled={disabled}
            statsigName={`${statsigPrefix}-dismiss`}
          />
        )}
      </div>
    </div>
  );
};
